<template>
  <div v-if="authStore.showLicenseGate && authStore.isAuthenticated" class="license-gate">
    <div class="license-gate__overlay"></div>
    <div class="license-gate__panel">
      <div class="license-gate__header">
        <h2>License Agreement</h2>
        <p class="license-gate__subtitle">Please review and accept the terms to continue</p>
      </div>

      <div class="license-gate__content">
        <div class="license-gate__scroll" ref="scrollContainer">
          <h3>Software License Agreement</h3>
          <p><strong>Effective Date:</strong> April 11, 2026</p>

          <h4>1. Grant of License</h4>
          <p>Subject to the terms and conditions of this Agreement, FinnAstro grants you a limited, non-exclusive, non-transferable, revocable license to access and use the FinnAstro platform and associated services.</p>

          <h4>2. Financial Disclaimer</h4>
          <p><strong>IMPORTANT:</strong> FinnAstro is an educational and entertainment platform. All content, signals, timing windows, and analysis provided are for informational and educational purposes only. Nothing on this platform constitutes financial, investment, trading, or legal advice.</p>
          <p>Past performance, backtest results, and historical analyses do not guarantee future results. Astrological market analysis is speculative and should not be the sole basis for any financial decision. You should always consult with a qualified financial advisor before making investment decisions.</p>

          <h4>3. No Guarantee of Returns</h4>
          <p>FinnAstro makes no representation, warranty, or guarantee regarding the accuracy, completeness, or timeliness of any market timing signals, price predictions, or investment recommendations. All expected returns shown are hypothetical and based on historical backtests, not future projections.</p>

          <h4>4. Risk Acknowledgment</h4>
          <p>You acknowledge that trading in financial markets, including commodities, stocks, and cryptocurrencies, involves substantial risk of loss. You accept full responsibility for any trading or investment decisions you make, and you agree that FinnAstro shall not be liable for any losses incurred.</p>

          <h4>5. Restrictions</h4>
          <p>You may not: (a) reverse engineer, decompile, or disassemble the platform; (b) redistribute, sell, lease, or sublicense access to the platform; (c) use the platform for any unlawful purpose; (d) use the platform to compete directly with FinnAstro's services.</p>

          <h4>6. Ownership</h4>
          <p>All intellectual property, including but not limited to software, algorithms, methodologies, content, and data, remains the exclusive property of FinnAstro and its licensors.</p>

          <h4>7. Limitation of Liability</h4>
          <p>To the maximum extent permitted by law, FinnAstro shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.</p>

          <h4>8. Indemnification</h4>
          <p>You agree to indemnify and hold harmless FinnAstro from any claims, damages, losses, or expenses arising from your use of the platform or violation of this Agreement.</p>

          <h4>9. Regulatory Compliance</h4>
          <p>FinnAstro is not a registered broker-dealer, investment adviser, or financial planner. The platform does not execute trades or hold customer funds. You are solely responsible for ensuring your use of the platform complies with all applicable laws and regulations in your jurisdiction.</p>

          <h4>10. Termination</h4>
          <p>FinnAstro reserves the right to suspend or terminate your access at any time, with or without cause, and with or without notice.</p>

          <h4>11. Modifications</h4>
          <p>FinnAstro may modify this Agreement at any time. Continued use of the platform after modifications constitutes acceptance of the revised terms.</p>

          <h4>12. Governing Law</h4>
          <p>This Agreement shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.</p>

          <p class="license-gate__acknowledgment"><strong>BY TYPING YOUR NAME BELOW AND CLICKING "I AGREE", YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THE TERMS OF THIS AGREEMENT.</strong></p>
        </div>
      </div>

      <div class="license-gate__accept">
        <div class="license-gate__field">
          <label for="license-name">Type your full name to sign</label>
          <input
            id="license-name"
            v-model="fullName"
            type="text"
            placeholder="Enter your full legal name"
            autocomplete="name"
            :disabled="isSubmitting"
          />
        </div>
        <button
          class="license-gate__agree-btn"
          :disabled="!fullName.trim() || isSubmitting"
          @click="handleAccept"
        >
          {{ isSubmitting ? 'Please wait...' : 'I Agree & Accept' }}
        </button>
        <p class="license-gate__note">
          Your signed name will be stored as your digital acceptance of this agreement.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useAuthStore } from '../stores/auth';

const authStore = useAuthStore();
const fullName = ref('');
const isSubmitting = ref(false);
const scrollContainer = ref(null);

const handleAccept = async () => {
  if (!fullName.value.trim() || isSubmitting.value) return;
  isSubmitting.value = true;
  const success = await authStore.acceptLicense(fullName.value.trim());
  if (!success) {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.license-gate {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
}

.license-gate__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.license-gate__panel {
  position: relative;
  width: 90%;
  max-width: 560px;
  max-height: 90vh;
  background: var(--bg-primary, #1a1a2e);
  border-radius: 16px;
  border: 1px solid var(--border-color, #2a2a3e);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}

.license-gate__header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #2a2a3e);
  text-align: center;
}

.license-gate__header h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary, #e0e0e0);
}

.license-gate__subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.82rem;
  color: var(--text-secondary, #888);
}

.license-gate__content {
  flex: 1;
  overflow: hidden;
}

.license-gate__scroll {
  padding: 1.25rem 1.5rem;
  max-height: 45vh;
  overflow-y: auto;
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--text-secondary, #aaa);
}

.license-gate__scroll h3 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary, #e0e0e0);
  margin: 0 0 0.75rem;
}

.license-gate__scroll h4 {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text-primary, #e0e0e0);
  margin: 1rem 0 0.35rem;
}

.license-gate__scroll p {
  margin: 0 0 0.5rem;
}

.license-gate__acknowledgment {
  margin-top: 1.25rem !important;
  padding: 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  border: 1px solid rgba(234, 179, 8, 0.25);
  border-radius: 8px;
  font-size: 0.8rem;
  color: var(--warning, #eab308);
}

.license-gate__accept {
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--border-color, #2a2a3e);
  background: var(--bg-secondary, #16162a);
}

.license-gate__field {
  margin-bottom: 0.75rem;
}

.license-gate__field label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary, #888);
  margin-bottom: 0.35rem;
}

.license-gate__field input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border-color, #2a2a3e);
  border-radius: 8px;
  background: var(--bg-tertiary, #0f0f1e);
  color: var(--text-primary, #e0e0e0);
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.license-gate__field input:focus {
  border-color: var(--accent, #c9a84c);
}

.license-gate__field input::placeholder {
  color: var(--text-dim, #555);
}

.license-gate__agree-btn {
  width: 100%;
  padding: 0.7rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #c9a84c, #a08030);
  color: #1a1a2e;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  font-family: inherit;
}

.license-gate__agree-btn:hover:not(:disabled) {
  opacity: 0.92;
}

.license-gate__agree-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.license-gate__agree-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.license-gate__note {
  margin: 0.5rem 0 0;
  font-size: 0.72rem;
  color: var(--text-dim, #555);
  text-align: center;
}

/* Scrollbar styling */
.license-gate__scroll::-webkit-scrollbar {
  width: 6px;
}

.license-gate__scroll::-webkit-scrollbar-track {
  background: transparent;
}

.license-gate__scroll::-webkit-scrollbar-thumb {
  background: var(--border-color, #2a2a3e);
  border-radius: 3px;
}

/* Mobile */
@media (max-width: 640px) {
  .license-gate__panel {
    width: 95%;
    max-height: 95vh;
    border-radius: 12px;
  }

  .license-gate__scroll {
    max-height: 38vh;
    padding: 1rem;
  }

  .license-gate__header {
    padding: 1rem;
  }

  .license-gate__accept {
    padding: 1rem;
  }
}
</style>
